'use client'

import { Check, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { instrumentKindTag, instrumentRank } from '@/features/hedge/lib/instrument-display'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { Instrument } from '@/types/generated/common/types'

// Cap search results so a large instrument universe stays snappy; typing narrows it.
const MAX_RESULTS = 100

interface HedgePositionGroupPickerProps {
  selected: string[]
  positionSymbolOptions: string[]
  error?: string
  onChange: (symbols: string[]) => void
  // Positions here can already span multiple exchanges (allPositionsAtom is
  // cross-account), and typed search adds "any instrument" beyond just open
  // positions — flattened across every exchange like the rest of the hedge
  // feature's symbol-identity model (same accepted same-symbol-on-two-
  // exchanges ambiguity `resolveDefaultHedgeInstrument` already lives with).
  instruments: Instrument[]
  // Replaces the search box's surface (border/bg/height/text size) so a host form can match
  // its own fields.
  fieldClassName?: string
}

interface Row {
  symbol: string
  tag: string
}

export function HedgePositionGroupPicker({
  selected,
  positionSymbolOptions,
  error,
  onChange,
  instruments,
  fieldClassName,
}: HedgePositionGroupPickerProps) {
  const [query, setQuery] = useState('')

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const bySymbol = useMemo(() => new Map(instruments.map((i) => [i.symbol, i])), [instruments])

  // Default view = your open positions. Once you type, search the whole instrument
  // universe so a leg can be added before the position exists.
  const rows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      return positionSymbolOptions.map((symbol) => {
        const inst = bySymbol.get(symbol)
        return { symbol, tag: inst ? instrumentKindTag(inst) : '' }
      })
    }
    return instruments
      .filter((i) => i.isActive && i.symbol.toLowerCase().includes(q))
      .sort((a, b) => instrumentRank(a) - instrumentRank(b) || a.symbol.localeCompare(b.symbol))
      .slice(0, MAX_RESULTS)
      .map((i) => ({ symbol: i.symbol, tag: instrumentKindTag(i) }))
  }, [query, instruments, positionSymbolOptions, bySymbol])

  const toggle = (symbol: string) => {
    const next = new Set(selectedSet)
    if (next.has(symbol)) next.delete(symbol)
    else next.add(symbol)
    onChange(Array.from(next))
  }

  return (
    <div className="flex flex-col gap-1">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((symbol) => (
            <span
              key={symbol}
              className="bg-emphasis text-emphasis-foreground flex items-center gap-1 px-1.5 py-0.5 text-[10px]"
            >
              <span className="truncate">{formatSymbol(symbol)}</span>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => toggle(symbol)}
                className="hover:text-emphasis-foreground/70"
                title={`Remove ${formatSymbol(symbol)}`}
              >
                <X className="size-2.5" />
              </Button>
            </span>
          ))}
        </div>
      )}

      <div
        className={cn(
          'focus-within:border-foreground/40 flex items-center gap-2 border transition-colors',
          fieldClassName ?? 'border-foreground/10 h-8 px-2.5 text-xs'
        )}
      >
        <Search className="text-muted-foreground size-3.5 shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search positions or any instrument…"
          className="placeholder:text-muted-foreground h-full w-full bg-transparent outline-none"
        />
      </div>

      <div className="border-foreground/10 max-h-40 overflow-y-auto border p-1">
        {rows.length === 0 ? (
          <p className="text-foreground/40 px-2 py-1 text-xs">
            {query.trim()
              ? 'No matching instruments'
              : 'No open positions - search to add any instrument'}
          </p>
        ) : (
          rows.map(({ symbol, tag }) => {
            const isSelected = selectedSet.has(symbol)
            return (
              <button
                key={symbol}
                type="button"
                onClick={() => toggle(symbol)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-xs',
                  isSelected ? 'bg-emphasis text-emphasis-foreground' : 'hover:bg-foreground/5'
                )}
              >
                <span className="truncate">{formatSymbol(symbol)}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {tag && <span className="text-muted-foreground text-[10px]">{tag}</span>}
                  {isSelected && <Check className="size-3" />}
                </span>
              </button>
            )
          })
        )}
      </div>

      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  )
}
